import { createSvgEl } from '@learnforge/demo-render';
import { type Expr } from '../algebra/expr';

export type TileKind = 'x' | 'x2' | 'unit';
export interface Tile {
  kind: TileKind;
  sign: 1 | -1;
  multiplicity: number;
  id: string;
}
export interface TilePlacement {
  kind: TileKind;
  sign: 1 | -1;
  x: number;
  y: number;
}
export type BalanceOp =
  | { kind: 'add'; value: Expr }
  | { kind: 'sub'; value: Expr }
  | { kind: 'mul'; value: Expr }
  | { kind: 'div'; value: Expr }
  | { kind: 'square' };
export interface BalanceScaleOptions {
  leftPan: readonly Tile[];
  rightPan: readonly Tile[];
  numericXForTilt?: number;
  width: number;
  height: number;
}
export interface BalanceScaleHandle {
  group: SVGGElement;
  update(o: Partial<BalanceScaleOptions>): void;
  destroy(): void;
}

const PAN_HALF = 55; // plate half-width → plate width 110
const TILE_GAP = 2;
const STRING_DROP = 40; // beam end → plate top
const MAX_ANGLE = 14; // was 30 — keep the tilt readable

function tileWidth(kind: TileKind): number {
  return kind === 'unit' ? 22 : 46;
}
function tileHeight(kind: TileKind): number {
  return kind === 'x2' ? 46 : 22;
}

export function layoutPanTiles(tiles: readonly Tile[]): TilePlacement[] {
  const instances: { kind: TileKind; sign: 1 | -1 }[] = [];
  for (const t of tiles) {
    for (let i = 0; i < t.multiplicity; i++) instances.push({ kind: t.kind, sign: t.sign });
  }
  const rows: { kind: TileKind; sign: 1 | -1 }[][] = [];
  let row: { kind: TileKind; sign: 1 | -1 }[] = [];
  let rowW = 0;
  for (const inst of instances) {
    const w = tileWidth(inst.kind) + (row.length > 0 ? TILE_GAP : 0);
    if (row.length > 0 && rowW + w > 2 * PAN_HALF - 4) {
      rows.push(row);
      row = [inst];
      rowW = tileWidth(inst.kind);
    } else {
      row.push(inst);
      rowW += w;
    }
  }
  if (row.length > 0) rows.push(row);
  const placements: TilePlacement[] = [];
  let bottom = 0; // plate top is y=0 in pan-local coords; rows grow upward (negative y)
  for (const r of rows) {
    const rowH = Math.max(...r.map((i) => tileHeight(i.kind))) + TILE_GAP;
    const rowWidth = r.reduce((s, i) => s + tileWidth(i.kind), 0) + TILE_GAP * (r.length - 1);
    let x = -rowWidth / 2;
    for (const inst of r) {
      placements.push({ kind: inst.kind, sign: inst.sign, x, y: bottom - rowH + TILE_GAP });
      x += tileWidth(inst.kind) + TILE_GAP;
    }
    bottom -= rowH;
  }
  return placements;
}

function weightOf(kind: TileKind, numericXForTilt: number): number {
  switch (kind) {
    case 'unit':
      return 1;
    case 'x':
      return numericXForTilt;
    case 'x2':
      return numericXForTilt * numericXForTilt;
  }
}

function renderTile(kind: TileKind, sign: 1 | -1): SVGGElement {
  const g = createSvgEl('g', {
    'data-kind': kind,
    'data-sign': sign.toString(),
  });

  const baseClass = sign === 1 ? `alg-tile-${kind}` : 'alg-tile-neg';

  if (kind === 'unit') {
    const rect = createSvgEl('rect', {
      x: 0,
      y: 0,
      width: 22,
      height: 22,
      class: baseClass,
    });
    g.appendChild(rect);
    const label = createSvgEl('text', {
      x: 11,
      y: 16,
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
      class: 'alg-tile-label',
    });
    label.textContent = '1';
    g.appendChild(label);
  } else if (kind === 'x') {
    const rect = createSvgEl('rect', {
      x: 0,
      y: 0,
      width: 46,
      height: 22,
      class: baseClass,
    });
    g.appendChild(rect);
    const label = createSvgEl('text', {
      x: 23,
      y: 16,
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
      class: 'alg-tile-label',
    });
    label.textContent = 'x';
    g.appendChild(label);
  } else if (kind === 'x2') {
    const rect = createSvgEl('rect', {
      x: 0,
      y: 0,
      width: 46,
      height: 46,
      class: baseClass,
    });
    g.appendChild(rect);
    const label = createSvgEl('text', {
      x: 23,
      y: 28,
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
      class: 'alg-tile-label',
    });
    label.textContent = 'x²';
    g.appendChild(label);
  }

  return g;
}

function computePanWeight(tiles: readonly Tile[], numericXForTilt: number): number {
  let weight = 0;
  for (const tile of tiles) {
    weight += tile.sign * tile.multiplicity * weightOf(tile.kind, numericXForTilt);
  }
  return weight;
}

function tanhApprox(x: number): number {
  const e2x = Math.exp(2 * x);
  return (e2x - 1) / (e2x + 1);
}

function computeTiltAngle(leftWeight: number, rightWeight: number): number {
  const scale = 5;
  const diff = rightWeight - leftWeight;
  const tilt = tanhApprox(diff / scale);
  return MAX_ANGLE * tilt;
}

function renderPan(panGroup: SVGGElement, tiles: readonly Tile[]): void {
  panGroup.innerHTML = '';

  const leftString = createSvgEl('line', {
    x1: 0,
    y1: 0,
    x2: -44,
    y2: STRING_DROP,
    class: 'alg-rod',
  });
  panGroup.appendChild(leftString);

  const rightString = createSvgEl('line', {
    x1: 0,
    y1: 0,
    x2: 44,
    y2: STRING_DROP,
    class: 'alg-rod',
  });
  panGroup.appendChild(rightString);

  const plate = createSvgEl('rect', {
    x: -PAN_HALF,
    y: STRING_DROP,
    width: 110,
    height: 6,
    rx: 3,
    class: 'alg-pan',
  });
  panGroup.appendChild(plate);

  for (const p of layoutPanTiles(tiles)) {
    const tileEl = renderTile(p.kind, p.sign);
    tileEl.setAttribute('transform', `translate(${p.x}, ${STRING_DROP + p.y})`);
    panGroup.appendChild(tileEl);
  }
}

export function createBalanceScale(
  parent: SVGSVGElement | SVGGElement,
  opts: BalanceScaleOptions,
): BalanceScaleHandle {
  const group = createSvgEl('g');
  parent.appendChild(group);

  const currentOpts = {
    leftPan: Array.from(opts.leftPan),
    rightPan: Array.from(opts.rightPan),
    numericXForTilt: opts.numericXForTilt ?? 0,
    width: opts.width,
    height: opts.height,
  };

  const pivotX = currentOpts.width / 2;
  const pivotY = currentOpts.height * 0.5;
  const beamHalf = currentOpts.width * 0.3;

  const beamGroup = createSvgEl('g', {
    'data-role': 'beam',
  });
  group.appendChild(beamGroup);

  const beam = createSvgEl('line', {
    x1: -beamHalf,
    y1: 0,
    x2: beamHalf,
    y2: 0,
    class: 'alg-beam',
  });
  beamGroup.appendChild(beam);

  const fulcrum = createSvgEl('polygon', {
    points: `${pivotX},${pivotY} ${pivotX - 28},${currentOpts.height * 0.8} ${pivotX + 28},${currentOpts.height * 0.8}`,
    'data-role': 'fulcrum',
    class: 'alg-fulcrum',
  });
  group.appendChild(fulcrum);

  const leftPanGroup = createSvgEl('g', {
    'data-role': 'pan',
    'data-side': 'left',
  });
  group.appendChild(leftPanGroup);

  const rightPanGroup = createSvgEl('g', {
    'data-role': 'pan',
    'data-side': 'right',
  });
  group.appendChild(rightPanGroup);

  const updatePanDisplay = (): void => {
    const leftWeight = computePanWeight(currentOpts.leftPan, currentOpts.numericXForTilt);
    const rightWeight = computePanWeight(currentOpts.rightPan, currentOpts.numericXForTilt);
    const angle = computeTiltAngle(leftWeight, rightWeight);
    beamGroup.setAttribute(
      'transform',
      `translate(${pivotX} ${pivotY}) rotate(${angle.toFixed(2)} 0 0)`,
    );

    const thetaR = (angle * Math.PI) / 180;
    const leftEx = pivotX - beamHalf * Math.cos(thetaR);
    const leftEy = pivotY - beamHalf * Math.sin(thetaR);
    const rightEx = pivotX + beamHalf * Math.cos(thetaR);
    const rightEy = pivotY + beamHalf * Math.sin(thetaR);

    leftPanGroup.setAttribute('transform', `translate(${leftEx}, ${leftEy})`);
    rightPanGroup.setAttribute('transform', `translate(${rightEx}, ${rightEy})`);

    renderPan(leftPanGroup, currentOpts.leftPan);
    renderPan(rightPanGroup, currentOpts.rightPan);
  };

  updatePanDisplay();

  return {
    group,

    update(partial: Partial<BalanceScaleOptions>): void {
      if (partial.leftPan !== undefined) {
        currentOpts.leftPan = Array.from(partial.leftPan);
      }
      if (partial.rightPan !== undefined) {
        currentOpts.rightPan = Array.from(partial.rightPan);
      }
      if (partial.numericXForTilt !== undefined) {
        currentOpts.numericXForTilt = partial.numericXForTilt;
      }
      if (partial.width !== undefined) {
        currentOpts.width = partial.width;
      }
      if (partial.height !== undefined) {
        currentOpts.height = partial.height;
      }
      updatePanDisplay();
    },

    destroy(): void {
      if (group.parentNode) {
        group.parentNode.removeChild(group);
      }
    },
  } satisfies BalanceScaleHandle;
}
